import React from 'react'
import { Grid, Typography, Tooltip } from '@the-platform-group/component-library'
import { HelpOutline as HelpOutlineIcon } from '@the-platform-group/component-library/Icons'
import { StyledCell } from './ComparisonRow'

/**
 * ProfileComparisonTable - Reusable table for profile field comparisons
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.fields - Array of field definitions
 * @param {Function} props.renderFieldValue - Function to render field value(s)
 * @param {Function} props.renderSpecialField - Optional function for special field rendering (e.g., multipleJobs)
 * @param {Array<string>} props.headers - Array of header labels
 * @param {Array<number>} props.columnWidths - Array of column widths (xs values, must sum to 12)
 * @returns {JSX.Element}
 */
const ProfileComparisonTable = ({
  fields,
  renderFieldValue,
  renderSpecialField,
  headers,
  columnWidths,
}) => {
  return (
    <Grid container>
      {/* Table Header */}
      <Grid
        item
        container
        sx={{
          backgroundColor: 'background.secondary.default',
          borderBottom: '1px solid',
          borderColor: 'border.interactive',
        }}
      >
        {headers.map((header, index) => (
          <Grid
            key={index}
            item
            xs={columnWidths[index]}
            sx={{ py: 2, px: header ? 2 : 0, alignItems: 'center' }}
          >
            {header && (
              <Typography variant="body2" fontWeight="bold">
                {header}
              </Typography>
            )}
          </Grid>
        ))}
      </Grid>

      {/* Table Rows */}
      {fields.map(field => {
        // Check if this field needs special rendering (e.g., multipleJobs accordion)
        if (renderSpecialField) {
          const specialRender = renderSpecialField(field)
          if (specialRender) {
            return <React.Fragment key={field.key}>{specialRender}</React.Fragment>
          }
        }

        // Default row rendering
        return (
          <Grid
            key={field.key}
            item
            container
            sx={{ borderBottom: '1px solid', borderColor: 'border.secondary' }}
          >
            {/* Field Name Column */}
            <StyledCell filled item xs={columnWidths[0]} sx={{ p: 1.5, display: 'block' }}>
              <Grid container alignItems="center" spacing={0.5}>
                <Grid item>
                  <Typography variant="body2">{field.label}</Typography>
                </Grid>
                {field.tooltip && (
                  <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={field.tooltip} placement="top">
                      <HelpOutlineIcon
                        sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }}
                      />
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </StyledCell>

            {/* Value Column(s) - rendered by the parent */}
            {renderFieldValue(field)}
          </Grid>
        )
      })}
    </Grid>
  )
}

export default ProfileComparisonTable
